import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useLang } from '../../lib/lang';
import {
  getAdminRaffles, createAdminRaffle, updateAdminRaffle, deleteAdminRaffle,
  drawWinner, designateWinner, getAdminRaffleEntries,
  setRaffleFeatured, unsetRaffleFeatured,
  exportRaffleTickets, closeRaffle,
  getPackageTemplates, createPackageTemplate, deletePackageTemplate,
  resendRaffleLaunchEmailAud, sendRaffleTicketsLeftEmailAud,
  resendRaffleSoldOutEmailAud, resendRaffleDrawDateEmailAud,
} from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Trophy, Eye, EyeOff, Upload, X, ImagePlus, Star, Ban, Users, Sparkles, Check, FileSpreadsheet, Lock, Save, BookmarkPlus, Mail, Send, CalendarClock, Megaphone } from 'lucide-react';
import EmailPreviewDialog from '../../components/admin/EmailPreviewDialog';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Reusable styles to ensure full visibility on cream Dialog content
const inputCls = 'rounded-xl bg-white !text-rolex-green';
const taCls = 'w-full bg-white border border-[hsl(var(--gold)/0.3)] rounded-xl p-3 text-sm resize-none !text-rolex-green focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold)/0.4)]';

export default function AdminRaffles() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRaffle, setEditRaffle] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingQuestion, setUploadingQuestion] = useState(false);
  const fileInputRef = useRef(null);
  const questionFileRef = useRef(null);

  // Designation dialog state
  const [designateDialog, setDesignateDialog] = useState({ open: false, raffle: null });
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [selectedWinnerUser, setSelectedWinnerUser] = useState(null);
  const [selectedTicketNumber, setSelectedTicketNumber] = useState(null);
  const [participantSearch, setParticipantSearch] = useState('');
  const [designating, setDesignating] = useState(false);
  const [winningCodeInput, setWinningCodeInput] = useState('');
  const [designateMode, setDesignateMode] = useState('code'); // 'code' | 'manual'
  const [templates, setTemplates] = useState([]);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [notifyDialog, setNotifyDialog] = useState({ open: false, payload: null });

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) { navigate('/admin/login'); return; }
    loadRaffles();
    loadTemplates();
  }, [user, isAdmin, navigate, authLoading]);

  const loadTemplates = async () => {
    try { const res = await getPackageTemplates(); setTemplates(res.data.templates || []); }
    catch { /* silent — templates are optional */ }
  };

  const applyTemplate = (tpl) => {
    setForm(prev => ({ ...prev, ticket_packages: [...(tpl.packages || [])] }));
    setShowTemplateMenu(false);
    toast.success(`${t('templateApplied') || 'Template applied'}: ${tpl.name}`);
  };

  const saveAsTemplate = async () => {
    if (!form.ticket_packages || form.ticket_packages.length === 0) {
      toast.error(t('noPackagesToSave') || 'No packages to save');
      return;
    }
    const name = window.prompt(t('templateNamePrompt') || 'Template name (e.g. "Standard 5/20/50/100"):');
    if (!name || !name.trim()) return;
    try {
      await createPackageTemplate(name.trim(), form.ticket_packages);
      toast.success(t('templateSaved') || 'Template saved');
      loadTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || t('toastFailed')); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm(t('confirmDeleteTemplate') || 'Delete this template?')) return;
    try { await deletePackageTemplate(id); toast.success(t('toastDeleted')); loadTemplates(); }
    catch { toast.error(t('toastFailed')); }
  };

  const [form, setForm] = useState({
    title: '', description: '', product_value: '', ticket_cost_points: '',
    max_tickets: '', max_winners: '1', imageUrls: [], video_url: '', category: 'watches',
    ends_at: '', enabled: true,
    q_enabled: false, q_text: '', q_a1: '', q_a2: '', q_a3: '', q_a4: '', q_correct: '0', q_image: '',
    ticket_packages: [],
    specs: {},
    shipping_info: '', rules_info: '',
    featured: false, force_sold_out: false, displayed_sold_tickets: '',
    max_tickets_per_user: '',
    status: 'active',
    draw_date: '',
  });

  const CATEGORY_SPECS = {
    watches: ['brand', 'model', 'reference_number', 'movement', 'year', 'caliber', 'glass', 'bezel_material', 'bracelet_material', 'papers'],
    handbags: ['brand', 'model', 'reference', 'material', 'color', 'size', 'year', 'hardware', 'papers'],
    jewelry: ['brand', 'type', 'material', 'stones', 'carat', 'certification', 'papers'],
    electronics: ['items_included', 'brand', 'color', 'storage', 'warranty'],
    travel: ['destination', 'duration', 'hotel', 'stars', 'included', 'departure_from', 'valid_until'],
    vehicles: ['brand', 'model', 'year', 'engine', 'mileage', 'color', 'transmission', 'papers'],
    other: ['brand', 'model', 'details'],
  };

  const SPEC_LABELS = {
    brand: t('specBrand'), model: t('specModel'), reference_number: t('specRefNumber'), movement: t('specMovement'),
    year: t('specYear'), caliber: t('specCaliber'), glass: t('specGlass'), bezel_material: t('specBezelMaterial'),
    bracelet_material: t('specBraceletMaterial'), papers: t('specPapers'),
    reference: t('specReference'), material: t('specMaterial'), color: t('specColor'), size: t('specSize'),
    hardware: t('specHardware'), type: t('specType'), stones: t('specStones'),
    carat: t('specCarat'), certification: t('specCertification'),
    items_included: t('specItemsIncluded'), storage: t('specStorage'), warranty: t('specWarranty'),
    destination: t('specDestination'), duration: t('specDuration'), hotel: t('specHotel'),
    stars: t('specStars'), included: t('specIncluded'), departure_from: t('specDepartureFrom'),
    valid_until: t('specValidUntil'), engine: t('specEngine'), mileage: t('specMileage'),
    transmission: t('specTransmission'), details: t('specDetails'),
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) { navigate('/admin/login'); return; }
  }, [user, isAdmin, navigate, authLoading]);

  const loadRaffles = async () => {
    try { const res = await getAdminRaffles(); setRaffles(res.data.raffles || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const emptyForm = {
    title: '', description: '', product_value: '', ticket_cost_points: '',
    max_tickets: '', max_winners: '1', imageUrls: [], video_url: '', category: 'watches',
    ends_at: '', enabled: true,
    q_enabled: false, q_text: '', q_a1: '', q_a2: '', q_a3: '', q_a4: '', q_correct: '0', q_image: '',
    ticket_packages: [], specs: {}, shipping_info: '', rules_info: '',
    featured: false, force_sold_out: false, displayed_sold_tickets: '',
    max_tickets_per_user: '',
    status: 'active',
    draw_date: '',
  };

  const openCreate = () => {
    setEditRaffle(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditRaffle(r);
    const q = r.question || {};
    setForm({
      title: r.title || '', description: r.description || '',
      product_value: r.product_value ?? '',
      ticket_cost_points: r.ticket_cost_points ?? '',
      max_tickets: r.max_tickets ?? '',
      imageUrls: r.images || [], video_url: r.video_url || '',
      category: r.category || 'other',
      ends_at: r.ends_at ? new Date(r.ends_at).toISOString().slice(0, 16) : '',
      enabled: r.enabled !== false,
      q_enabled: q.enabled || false, q_text: q.question || '',
      q_a1: q.answers?.[0] || '', q_a2: q.answers?.[1] || '',
      q_a3: q.answers?.[2] || '', q_a4: q.answers?.[3] || '',
      q_correct: String(q.correct_answer || 0), q_image: q.image || '',
      ticket_packages: r.ticket_packages || [],
      specs: r.specs || {},
      shipping_info: r.shipping_info || '',
      rules_info: r.rules_info || '',
      max_winners: String(r.max_winners || 1),
      featured: r.featured === true,
      force_sold_out: r.force_sold_out === true,
      displayed_sold_tickets: r.displayed_sold_tickets ?? '',
      max_tickets_per_user: r.max_tickets_per_user ?? '',
      status: r.status || 'active',
      draw_date: r.draw_date ? new Date(r.draw_date).toISOString().slice(0, 16) : '',
    });
    setDialogOpen(true);
  };

  const handleUploadImages = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) formData.append('files', files[i]);
      const token = localStorage.getItem('winluxor_token');
      const res = await axios.post(`${API_BASE}/api/upload/multiple`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
      });
      const newUrls = (res.data.urls || []).map(u => `${API_BASE}${u}`);
      setForm(prev => ({ ...prev, imageUrls: [...prev.imageUrls, ...newUrls] }));
      toast.success(`${newUrls.length} ${t('imagesUploaded')}`);
    } catch { toast.error(t('toastUploadFailed')); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleUploadQuestionImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingQuestion(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('winluxor_token');
      const res = await axios.post(`${API_BASE}/api/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
      });
      setForm(prev => ({ ...prev, q_image: `${API_BASE}${res.data.url}` }));
      toast.success(t('questionImageUploaded'));
    } catch { toast.error(t('toastUploadFailed')); }
    finally { setUploadingQuestion(false); if (questionFileRef.current) questionFileRef.current.value = ''; }
  };

  const removeImage = (idx) => {
    setForm(prev => ({ ...prev, imageUrls: prev.imageUrls.filter((_, i) => i !== idx) }));
  };

  const addImageUrl = () => {
    const url = prompt(t('enterImageUrl'));
    if (url && url.trim()) setForm(prev => ({ ...prev, imageUrls: [...prev.imageUrls, url.trim()] }));
  };

  const handleSubmit = async () => {
    // Client-side validation — surface user-friendly messages instead of letting
    // the backend reject with a confusing 422.
    const productValue = parseFloat(form.product_value);
    const ticketCost = parseInt(form.ticket_cost_points);
    const maxTickets = parseInt(form.max_tickets);
    const errs = [];
    if (!form.title || !form.title.trim()) errs.push(t('titleRequired') || 'Title required');
    if (!form.description || !form.description.trim()) errs.push(t('descRequired') || 'Description required');
    if (isNaN(productValue) || productValue <= 0) errs.push(t('productValueInvalid') || 'Product value must be > 0');
    if (isNaN(ticketCost) || ticketCost <= 0) errs.push(t('ticketCostInvalid') || 'Ticket cost must be > 0');
    if (isNaN(maxTickets) || maxTickets <= 0) errs.push(t('maxTicketsInvalid') || 'Max tickets must be > 0');
    if (form.q_enabled) {
      if (!form.q_text || !form.q_text.trim()) errs.push(t('questionTextRequired') || 'Question text required');
      const answers = [form.q_a1, form.q_a2, form.q_a3, form.q_a4].filter((x) => x && x.trim());
      if (answers.length < 2) errs.push(t('atLeast2Answers') || 'At least 2 answers required');
      const correctIdx = parseInt(form.q_correct);
      if (isNaN(correctIdx) || correctIdx < 0 || correctIdx >= answers.length) {
        errs.push(t('selectCorrectAnswer') || 'Select the correct answer');
      }
    }
    // ends_at must produce a valid ISO date
    let endsAtIso;
    try {
      endsAtIso = form.ends_at ? new Date(form.ends_at).toISOString() : new Date(Date.now() + 7 * 86400000).toISOString();
    } catch {
      errs.push(t('endDateInvalid') || 'End date is invalid');
      endsAtIso = new Date(Date.now() + 7 * 86400000).toISOString();
    }
    if (errs.length > 0) {
      toast.error(errs.join(' · '));
      return;
    }

    const question = form.q_enabled ? {
      question: form.q_text,
      answers: [form.q_a1, form.q_a2, form.q_a3, form.q_a4].filter(Boolean),
      correct_answer: parseInt(form.q_correct),
      enabled: true,
      image: form.q_image || null,
    } : null;
    const payload = {
      title: form.title, description: form.description,
      product_value: productValue,
      ticket_cost_points: ticketCost,
      max_tickets: maxTickets,
      images: form.imageUrls,
      video_url: form.video_url || null,
      category: form.category,
      ends_at: endsAtIso,
      enabled: form.enabled,
      question,
      ticket_packages: form.ticket_packages.length > 0 ? form.ticket_packages : null,
      specs: Object.keys(form.specs).length > 0 ? form.specs : null,
      shipping_info: form.shipping_info || null,
      rules_info: form.rules_info || null,
      max_winners: parseInt(form.max_winners) || 1,
      featured: !!form.featured,
      force_sold_out: !!form.force_sold_out,
      displayed_sold_tickets: form.displayed_sold_tickets === '' ? null : parseInt(form.displayed_sold_tickets),
      max_tickets_per_user: form.max_tickets_per_user === '' ? 0 : parseInt(form.max_tickets_per_user),
      status: form.status || 'active',
      // Custom advanced draw date for sold_out raffles.
      // Empty string explicitly CLEARS the date on the backend (admin doesn't
      // know yet); a valid ISO string sets it.
      draw_date: form.draw_date ? new Date(form.draw_date).toISOString() : '',
    };
    // For updates: if the change would trigger participant emails (sold-out
    // transition or draw date set/changed), ask the admin first.
    if (editRaffle) {
      const soldOutTransition = !editRaffle.force_sold_out && payload.force_sold_out;
      const oldDdStr = editRaffle.draw_date ? new Date(editRaffle.draw_date).toISOString().slice(0, 16) : '';
      const drawDateChanged = payload.force_sold_out && !!form.draw_date && form.draw_date !== oldDdStr;
      if (soldOutTransition || drawDateChanged) {
        setNotifyDialog({ open: true, payload });
        return;
      }
      await performSave(payload, false);
      return;
    }
    try {
      await createAdminRaffle(payload); toast.success(t('raffleCreated'));
      setDialogOpen(false);
      await loadRaffles();
    } catch (e) {
      // Convert FastAPI Pydantic validation error arrays into a readable string
      const detail = e?.response?.data?.detail;
      let msg = 'Failed';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) {
        msg = detail.map((d) => {
          const field = Array.isArray(d?.loc) ? d.loc.slice(-1)[0] : (d?.loc || 'field');
          return `${field}: ${d?.msg || 'invalid'}`;
        }).join(' · ');
      } else if (detail && typeof detail === 'object') {
        msg = JSON.stringify(detail);
      }
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error('[handleSubmit raffle] error:', e);
    }
  };

  const performSave = async (payload, notify) => {
    try {
      await updateAdminRaffle(editRaffle._id, { ...payload, notify_participants: !!notify });
      toast.success(notify ? (t('raffleUpdatedEmailSent') || 'Updated — email sent to participants') : t('raffleUpdated'));
      setNotifyDialog({ open: false, payload: null });
      setDialogOpen(false);
      await loadRaffles();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDeleteRaffle'))) return;
    try { await deleteAdminRaffle(id); toast.success(t('toastDeleted')); loadRaffles(); }
    catch { toast.error(t('toastFailed')); }
  };

  const handleAutoDraw = async (id) => {
    if (!window.confirm(t('confirmAutoDraw'))) return;
    try {
      const res = await drawWinner(id);
      toast.success(`🏆 Winner: ${res.data.winner?.user_name}`);
      loadRaffles();
    } catch (e) { toast.error(e.response?.data?.detail || 'Draw failed'); }
  };

  const openDesignate = async (raffle) => {
    setDesignateDialog({ open: true, raffle });
    setSelectedWinnerUser(null);
    setSelectedTicketNumber(null);
    setParticipantSearch('');
    setWinningCodeInput('');
    setDesignateMode('code');
    setLoadingParticipants(true);
    try {
      const res = await getAdminRaffleEntries(raffle._id);
      setParticipants(res.data.participants || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load participants');
      setParticipants([]);
    } finally { setLoadingParticipants(false); }
  };

  const confirmDesignate = async () => {
    if (!designateDialog.raffle) return;
    setDesignating(true);
    try {
      if (designateMode === 'code') {
        if (!winningCodeInput.trim()) {
          toast.error(t('codeRequired') || 'Enter a winning ticket code');
          setDesignating(false);
          return;
        }
        await designateWinner(designateDialog.raffle._id, null, null, winningCodeInput.trim());
      } else {
        if (!selectedWinnerUser) {
          toast.error(t('selectUser') || 'Select a participant');
          setDesignating(false);
          return;
        }
        await designateWinner(designateDialog.raffle._id, selectedWinnerUser, selectedTicketNumber || null);
      }
      toast.success(t('designateWinnerToast'));
      setDesignateDialog({ open: false, raffle: null });
      loadRaffles();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('toastFailed'));
    } finally { setDesignating(false); }
  };

  const handleToggleFeatured = async (raffle) => {
    try {
      if (raffle.featured) {
        await unsetRaffleFeatured(raffle._id);
        toast.success(t('featuredRemoved'));
      } else {
        await setRaffleFeatured(raffle._id);
        toast.success(t('featuredAdded'));
      }
      loadRaffles();
    } catch (e) { toast.error(t('toastFailed')); }
  };

  // Quick toggle: show / hide this raffle from the public site.
  // When hidden (enabled=false), only admins can see it (via direct URL).
  // Used to create draft / test raffles without making them visible.
  const handleToggleVisible = async (raffle) => {
    const newEnabled = !raffle.enabled;
    try {
      await updateAdminRaffle(raffle._id, { enabled: newEnabled });
      toast.success(newEnabled
        ? (t('raffleNowVisible') || '✅ Tirage visible publiquement')
        : (t('raffleNowHidden') || '🙈 Tirage masqué (mode test)'));
      loadRaffles();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('toastFailed'));
    }
  };

  const handleExportTickets = async (raffle) => {
    try {
      const res = await exportRaffleTickets(raffle._id);
      const cd = res.headers?.['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/i.exec(cd);
      const filename = (m && m[1]) || `tickets-${raffle.title}.csv`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      // Defer cleanup safely (some browsers detach the anchor automatically)
      setTimeout(() => {
        try {
          if (a.parentNode) a.parentNode.removeChild(a);
        } catch (_) { /* node already detached */ }
        URL.revokeObjectURL(url);
      }, 200);
      toast.success(t('exportSuccess') || 'Tickets exported');
    } catch (e) {
      toast.error(t('toastFailed'));
    }
  };

  const handleCloseRaffle = async (raffle) => {
    if (!window.confirm(t('confirmCloseRaffle') || 'Close this raffle now? Sales will stop and you can draw the winner.')) return;
    try {
      await closeRaffle(raffle._id);
      toast.success(t('raffleClosedToast') || 'Raffle closed (sales stopped)');
      loadRaffles();
    } catch (e) {
      toast.error(t('toastFailed'));
    }
  };

  // Track per-raffle sending state for spinner UI
  const [sendingEmailId, setSendingEmailId] = useState(null);
  // Email preview modal state
  const [emailPreview, setEmailPreview] = useState(null); // { raffle, type, label, icon, sendFn }

  const openEmailPreview = (raffle, type, label, icon, sendFn, ticketsOverride = null) => {
    setEmailPreview({ raffle, type, label, icon, sendFn, ticketsOverride });
  };
  const closeEmailPreview = () => setEmailPreview(null);

  const handleResendLaunch = (raffle) => {
    openEmailPreview(raffle, 'launch', 'Email de lancement', <Mail className="w-5 h-5 text-blue-600" />, async (audience) => {
      setSendingEmailId(raffle._id + ':launch');
      try {
        const res = await resendRaffleLaunchEmailAud(raffle._id, audience);
        toast.success(`✉️ ${res.data?.sent || 0} ${t('emailQueuedSuffix') || 'email(s) en file d\'envoi'}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || t('toastFailed') || 'Erreur');
        throw e;
      } finally {
        setSendingEmailId(null);
      }
    });
  };

  // Relance dialog: admin picks the "tickets left" number displayed in the email
  const [relanceDialog, setRelanceDialog] = useState(null); // { raffle, count, realLeft, max }

  const handleSendTicketsLeft = (raffle) => {
    const sold = raffle.sold_tickets || 0;
    const max = raffle.max_tickets || 1;
    const left = Math.max(0, max - sold);
    if (left === 0) {
      toast.error(t('noTicketsLeft') || 'Aucun ticket restant.');
      return;
    }
    setRelanceDialog({ raffle, count: String(left), realLeft: left, max });
  };

  const confirmRelanceCount = () => {
    if (!relanceDialog) return;
    const { raffle, count, max } = relanceDialog;
    const n = parseInt(count);
    if (isNaN(n) || n < 1 || n > max) {
      toast.error(`${t('invalidTicketCount') || 'Nombre invalide'} (1 – ${max})`);
      return;
    }
    setRelanceDialog(null);
    openEmailPreview(raffle, 'urgency', `${t('relanceEmail') || 'Email de relance'} — ${n} ${t('ticketsLeftLabel') || 'tickets restants'}`, <Send className="w-5 h-5 text-orange-600" />, async (audience) => {
      setSendingEmailId(raffle._id + ':urgency');
      try {
        const res = await sendRaffleTicketsLeftEmailAud(raffle._id, audience, n);
        toast.success(`🔥 ${res.data?.sent || 0} ${t('emailQueuedSuffix') || 'email(s) en file d\'envoi'} — ${res.data?.tickets_left} ${t('ticketsLeftLabel') || 'tickets restants'}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || t('toastFailed') || 'Erreur');
        throw e;
      } finally {
        setSendingEmailId(null);
      }
    }, n);
  };

  // Re-send the "sold out + advanced draw" announcement
  const handleResendSoldOut = (raffle) => {
    openEmailPreview(raffle, 'soldout', 'Sold Out — Tirage avancé', <Megaphone className="w-5 h-5 text-red-600" />, async (audience) => {
      setSendingEmailId(raffle._id + ':soldout');
      try {
        const res = await resendRaffleSoldOutEmailAud(raffle._id, audience);
        toast.success(`🎯 ${res.data?.sent || 0} ${t('participantsEmailedSuffix') || 'participants notifiés'}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || t('toastFailed') || 'Erreur');
        throw e;
      } finally {
        setSendingEmailId(null);
      }
    });
  };

  // Re-send the "new draw date announced" email
  const handleResendDrawDate = (raffle) => {
    if (!raffle.draw_date) {
      toast.error(t('noDrawDateSet') || 'Aucune date de tirage configurée. Renseignez-la d\'abord.');
      return;
    }
    openEmailPreview(raffle, 'drawdate', 'Date du tirage annoncée', <CalendarClock className="w-5 h-5 text-emerald-600" />, async (audience) => {
      setSendingEmailId(raffle._id + ':drawdate');
      try {
        const res = await resendRaffleDrawDateEmailAud(raffle._id, audience);
        toast.success(`📅 ${res.data?.sent || 0} ${t('participantsEmailedSuffix') || 'participants notifiés'}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || t('toastFailed') || 'Erreur');
        throw e;
      } finally {
        setSendingEmailId(null);
      }
    });
  };

  if (authLoading || !user || !isAdmin) return null;

  const filteredParticipants = participants.filter((p) => {
    if (!participantSearch) return true;
    const q = participantSearch.toLowerCase();
    return (
      (p.first_name + ' ' + p.last_name).toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="flex min-h-screen">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">{t('raffles')}</h1>
            <p className="text-muted-foreground mt-1 text-sm">{t('manageDraws')}</p>
          </div>
          <Button className="bg-primary text-primary-foreground btn-press gap-2 rounded-xl" onClick={openCreate} data-testid="admin-create-raffle-button">
            <Plus className="w-4 h-4" /> {t('newRaffle')}
          </Button>
        </div>

        <Card className="bg-white border-border" data-testid="admin-raffles-table"><CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('title')}</TableHead>
                <TableHead>{t('value')}</TableHead>
                <TableHead>{t('tickets')}</TableHead>
                <TableHead>{t('status')}</TableHead>
                <TableHead>{t('drawn')}</TableHead>
                <TableHead className="text-right">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{raffles.map((r) => {
              const realSold = r.sold_tickets || 0;
              const displayed = r.displayed_sold_tickets ?? null;
              const isSoldOut = (r.force_sold_out === true) || (realSold >= (r.max_tickets || 1));
              return (
                <TableRow key={r._id} className={r.drawn ? 'bg-yellow-50/40' : ''}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.featured && (
                        <Badge className="bg-gold-gradient !text-rolex-green border-none gap-1 text-[10px]" title="Featured on home">
                          <Star className="w-3 h-3" /> FEATURED
                        </Badge>
                      )}
                      <span>{r.title}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono">£{r.product_value?.toLocaleString()}</TableCell>
                  <TableCell className="font-mono text-xs">
                    <span className={isSoldOut ? 'text-red-600 font-bold' : ''}>{realSold}</span>/{r.max_tickets}
                    {displayed !== null && displayed !== undefined && displayed !== '' && (
                      <div className="text-[10px] text-blue-600 font-semibold mt-0.5" title="Displayed value (override)">
                        🎭 shows: {displayed}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {!r.enabled ? (
                      <Badge className="bg-orange-100 text-orange-700 border-orange-300 gap-1 text-[10px]"><EyeOff className="w-3 h-3" /> {t('hidden') || 'MASQUÉ'}</Badge>
                    ) : isSoldOut ? (
                      <Badge className="bg-red-100 text-red-700 border-red-300 gap-1 text-[10px]"><Ban className="w-3 h-3" /> SOLD OUT</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px]">{t('active')}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.drawn ? (
                      <Badge className="bg-primary/10 text-primary border-primary/20 gap-1 text-[10px]"><Trophy className="w-3 h-3" /> {t('winnerDrawn')}</Badge>
                    ) : (
                      <div className="flex gap-1 flex-wrap">
                        <Button size="sm" variant="outline" className="gap-1 text-[11px] rounded-lg h-7 px-2" onClick={() => openDesignate(r)} data-testid={`designate-${r._id}`} title="Designate winner manually">
                          <Sparkles className="w-3 h-3" /> {t('designate') || 'Designate'}
                        </Button>
                        <Button size="sm" variant="ghost" className="gap-1 text-[11px] rounded-lg h-7 px-2" onClick={() => handleAutoDraw(r._id)} title="Auto-draw random">
                          <Trophy className="w-3 h-3" /> Auto
                        </Button>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleToggleVisible(r)}
                        title={r.enabled ? (t('hideRaffle') || 'Masquer (mode test)') : (t('showRaffle') || 'Rendre visible')}
                        className={r.enabled ? 'text-green-600 hover:text-green-700' : 'text-orange-600 hover:text-orange-700'}
                        data-testid={`toggle-visible-${r._id}`}
                      >
                        {r.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => handleExportTickets(r)} title={t('exportTickets') || 'Export tickets CSV'} data-testid={`export-${r._id}`}>
                        <FileSpreadsheet className="w-4 h-4 text-green-700" />
                      </Button>
                      {!r.drawn && !isSoldOut && (
                        <Button size="icon" variant="ghost" onClick={() => handleCloseRaffle(r)} title={t('closeRaffleNow') || 'Close raffle now'}>
                          <Lock className="w-4 h-4 text-orange-600" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => handleToggleFeatured(r)} title={r.featured ? 'Remove from featured' : 'Mark as featured'} className={r.featured ? 'text-gold' : ''}>
                        <Star className={`w-4 h-4 ${r.featured ? 'fill-current' : ''}`} />
                      </Button>
                      {/* ── Email actions ── */}
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleResendLaunch(r)}
                        disabled={sendingEmailId === r._id + ':launch'}
                        title={t('resendLaunchEmail') || 'Renvoyer email de lancement à tous les clients'}
                        data-testid={`raffle-resend-launch-${r._id}`}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Mail className={`w-4 h-4 ${sendingEmailId === r._id + ':launch' ? 'animate-pulse' : ''}`} />
                      </Button>
                      {(() => {
                        const realSold = r.sold_tickets || 0;
                        const max = r.max_tickets || 1;
                        const ticketsLeft = Math.max(0, max - realSold);
                        const isSoldOut = (r.force_sold_out === true) || (realSold >= max);
                        const canSendUrgency = !isSoldOut && ticketsLeft > 0;
                        return (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleSendTicketsLeft(r)}
                              disabled={!canSendUrgency || sendingEmailId === r._id + ':urgency'}
                              title={canSendUrgency
                                ? `${t('sendUrgencyEmail') || 'Email urgence'} (${ticketsLeft} ${t('ticketsLeftLabel') || 'tickets restants'})`
                                : (t('noTicketsLeft') || 'Aucun ticket restant')}
                              data-testid={`raffle-send-urgency-${r._id}`}
                              className={canSendUrgency ? 'text-orange-600 hover:text-orange-700' : ''}
                            >
                              <Send className={`w-4 h-4 ${sendingEmailId === r._id + ':urgency' ? 'animate-pulse' : ''}`} />
                            </Button>
                            {isSoldOut && (
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleResendSoldOut(r)}
                                disabled={sendingEmailId === r._id + ':soldout'}
                                title={t('resendSoldOutEmail') || 'Renvoyer email "Tirage sold out — tirage avancé"'}
                                data-testid={`raffle-resend-soldout-${r._id}`}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Megaphone className={`w-4 h-4 ${sendingEmailId === r._id + ':soldout' ? 'animate-pulse' : ''}`} />
                              </Button>
                            )}
                            {isSoldOut && r.draw_date && (
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleResendDrawDate(r)}
                                disabled={sendingEmailId === r._id + ':drawdate'}
                                title={t('resendDrawDateEmail') || 'Renvoyer email "Date du tirage annoncée"'}
                                data-testid={`raffle-resend-drawdate-${r._id}`}
                                className="text-emerald-600 hover:text-emerald-700"
                              >
                                <CalendarClock className={`w-4 h-4 ${sendingEmailId === r._id + ':drawdate' ? 'animate-pulse' : ''}`} />
                              </Button>
                            )}
                          </>
                        );
                      })()}
                      <Button size="icon" variant="ghost" onClick={() => navigate(`/raffle/${r._id}`)} title="View"><Eye className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(r)} title="Edit"><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDelete(r._id)} title="Delete"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}</TableBody>
          </Table>
        </CardContent></Card>

        {/* CREATE / EDIT RAFFLE DIALOG */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white border-border max-w-2xl max-h-[90vh] overflow-y-auto" style={{ color: '#0B3D2E' }}>
            <DialogHeader>
              <DialogTitle className="font-display text-xl !text-rolex-green">{editRaffle ? t('editRaffle') : t('newRaffle')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2"><Label className="!text-rolex-green">{t('title')}</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className={inputCls} /></div>
              <div className="space-y-2"><Label className="!text-rolex-green">{t('description')}</Label><textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className={taCls + ' h-24'} /></div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="!text-rolex-green">{t('productValue')} (£)</Label><Input type="number" value={form.product_value} onChange={(e) => setForm({...form, product_value: e.target.value})} className={inputCls} /></div>
                <div className="space-y-2"><Label className="!text-rolex-green">{t('ticketCostPoints')}</Label><Input type="number" value={form.ticket_cost_points} onChange={(e) => setForm({...form, ticket_cost_points: e.target.value})} className={inputCls} /></div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="!text-rolex-green">{t('maxTickets')}</Label><Input type="number" value={form.max_tickets} onChange={(e) => setForm({...form, max_tickets: e.target.value})} className={inputCls} /></div>
                <div className="space-y-2">
                  <Label className="!text-rolex-green">{t('category')}</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v, specs: {}})}>
                    <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="watches">{t('watches')}</SelectItem>
                      <SelectItem value="handbags">{t('handbags')}</SelectItem>
                      <SelectItem value="jewelry">{t('jewelry')}</SelectItem>
                      <SelectItem value="electronics">{t('electronics')}</SelectItem>
                      <SelectItem value="travel">Travel</SelectItem>
                      <SelectItem value="vehicles">{t('vehicles')}</SelectItem>
                      <SelectItem value="other">{t('other')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="!text-rolex-green">{t('maxWinners')}</Label><Input type="number" value={form.max_winners} onChange={(e) => setForm({...form, max_winners: e.target.value})} className={inputCls} /></div>
                <div className="space-y-2"><Label className="!text-rolex-green">{t('endDate')}</Label><Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({...form, ends_at: e.target.value})} className={inputCls} /></div>
              </div>

              {/* Featured / Sold Out / Displayed override */}
              <div className="space-y-3 border-t border-[hsl(var(--gold)/0.2)] pt-4 bg-[hsl(43_30%_94%)] -mx-6 px-6 py-4">
                <Label className="font-semibold text-base !text-rolex-green flex items-center gap-1"><Star className="w-4 h-4 text-gold" /> {t('marketingOptions') || 'Marketing & Status'}</Label>

                <div className="flex items-center justify-between p-3 rounded-lg bg-white border border-[hsl(var(--gold)/0.3)]">
                  <div>
                    <Label className="!text-rolex-green text-sm font-semibold flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 text-gold" /> {t('featuredHomeLabel') || 'Featured on Homepage Hero'}
                    </Label>
                    <p className="text-[11px] text-rolex-green/65 mt-0.5">{t('featuredHomeHelp') || 'Only one raffle can be featured at a time. Marking this one will unmark any other.'}</p>
                  </div>
                  <Switch checked={form.featured} onCheckedChange={(v) => setForm({...form, featured: v})} data-testid="form-featured" />
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-white border border-[hsl(var(--gold)/0.3)]">
                  <div>
                    <Label className="!text-rolex-green text-sm font-semibold flex items-center gap-1">
                      <Ban className="w-3.5 h-3.5 text-red-500" /> {t('forceSoldOutLabel') || 'Force Sold Out'}
                    </Label>
                    <p className="text-[11px] text-rolex-green/65 mt-0.5">{t('forceSoldOutHelp') || 'Manually mark as sold out (stops new ticket purchases). Countdown to draw stays visible.'}</p>
                  </div>
                  <Switch checked={form.force_sold_out} onCheckedChange={(v) => setForm({...form, force_sold_out: v})} data-testid="form-force-sold-out" />
                </div>

                {/* Advanced draw date — only meaningful when sold out */}
                {form.force_sold_out && (
                  <div className="p-3 rounded-lg bg-gradient-to-br from-red-50 to-amber-50 border-2 border-red-300" data-testid="form-draw-date-block">
                    <Label className="!text-rolex-green text-sm font-semibold flex items-center gap-1 mb-1">
                      🎯 {t('advancedDrawDateLabel') || 'Date du tirage avancé'}
                    </Label>
                    <p className="text-[11px] text-rolex-green/75 mb-2.5 leading-snug">
                      {t('advancedDrawDateHelp') || 'Tirage avancé suite à un sold out anticipé. Laissez vide tant que vous ne connaissez pas la date : les joueurs verront "Date à venir prochainement". Une fois renseignée, un compte à rebours s\'affichera.'}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input
                        type="datetime-local"
                        value={form.draw_date}
                        onChange={(e) => setForm({...form, draw_date: e.target.value})}
                        className={inputCls + ' flex-1 min-w-[200px]'}
                        data-testid="form-draw-date"
                      />
                      {form.draw_date && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setForm({...form, draw_date: ''})}
                          className="border-red-300 text-red-700 hover:bg-red-100 gap-1.5 shrink-0"
                          data-testid="form-draw-date-clear"
                        >
                          <X className="w-3.5 h-3.5" /> {t('clearDate') || 'Effacer la date'}
                        </Button>
                      )}
                    </div>
                    {!form.draw_date ? (
                      <p className="text-[10px] text-amber-700 mt-2 italic flex items-start gap-1.5">
                        ⏳ {t('advancedDrawDateMissing') || 'Aucune date renseignée — l\'UI publique affiche "Tirage avancé · Date à venir prochainement".'}
                      </p>
                    ) : (
                      <p className="text-[10px] text-emerald-700 mt-2 italic flex items-start gap-1.5">
                        ✅ {t('advancedDrawDateSet') || 'Compte à rebours actif jusqu\'à cette date sur la fiche publique.'}
                      </p>
                    )}
                  </div>
                )}

                {/* Raffle status: Active vs Coming Soon */}
                <div className="p-3 rounded-lg bg-white border border-[hsl(var(--gold)/0.3)]">
                  <Label className="!text-rolex-green text-sm font-semibold flex items-center gap-1 mb-2">
                    🚀 {t('raffleStatusLabel') || 'Type de tirage'}
                  </Label>
                  <p className="text-[11px] text-rolex-green/65 mb-3">
                    {t('raffleStatusHelp') || 'Choisissez si ce tirage est déjà actif ou seulement annoncé pour plus tard.'}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, status: 'active' })}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${form.status === 'active' ? 'border-emerald-500 bg-emerald-50 shadow-md' : 'border-stone-200 hover:border-emerald-300'}`}
                      data-testid="form-status-active"
                    >
                      <div className="font-bold text-rolex-green text-sm flex items-center gap-1.5">
                        🟢 {t('statusActive') || 'Actif'}
                      </div>
                      <p className="text-[10px] text-rolex-green/65 mt-1">
                        {t('statusActiveHelp') || 'Tickets en vente. Prix + décompte affichés.'}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, status: 'coming_soon' })}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${form.status === 'coming_soon' ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-stone-200 hover:border-amber-300'}`}
                      data-testid="form-status-coming-soon"
                    >
                      <div className="font-bold text-rolex-green text-sm flex items-center gap-1.5">
                        🔜 {t('statusComingSoon') || 'Bientôt'}
                      </div>
                      <p className="text-[10px] text-rolex-green/65 mt-1">
                        {t('statusComingSoonHelp') || 'Annonce uniquement. Pas de prix, pas de date, achat bloqué.'}
                      </p>
                    </button>
                  </div>
                </div>

                <div className="space-y-2 p-3 rounded-lg bg-white border border-[hsl(var(--gold)/0.3)]">
                  <Label className="!text-rolex-green text-sm font-semibold flex items-center gap-1">
                    🎟️ {t('maxPerUserField') || 'Max tickets per player'}
                  </Label>
                  <p className="text-[11px] text-rolex-green/65">{t('maxPerUserHelp') || 'Set a hard cap on how many tickets a single user can buy on this raffle. Leave empty or 0 for unlimited.'}</p>
                  <Input
                    type="number"
                    min="0"
                    placeholder={t('unlimited') || '0 = unlimited'}
                    value={form.max_tickets_per_user}
                    onChange={(e) => setForm({...form, max_tickets_per_user: e.target.value})}
                    className={inputCls + ' w-full'}
                    data-testid="form-max-per-user"
                  />
                </div>

                <div className="space-y-2 p-3 rounded-lg bg-white border border-[hsl(var(--gold)/0.3)]">
                  <Label className="!text-rolex-green text-sm font-semibold flex items-center gap-1">
                    🎭 {t('displayedSoldTicketsLabel') || 'Displayed sold tickets (override)'}
                  </Label>
                  <p className="text-[11px] text-rolex-green/65">{t('displayedSoldTicketsHelp') || 'Optional. If set, public site shows this number instead of the real one (psychological boost). Real sold count is preserved.'}</p>
                  <Input
                    type="number"
                    min="0"
                    placeholder={t('leaveEmptyForReal') || 'Leave empty to show real count'}
                    value={form.displayed_sold_tickets}
                    onChange={(e) => setForm({...form, displayed_sold_tickets: e.target.value})}
                    className={inputCls + ' w-full'}
                    data-testid="form-displayed-sold-tickets"
                  />
                </div>
              </div>

              {/* Category Specifications */}
              <div className="space-y-3 border-t border-border pt-4">
                <Label className="font-semibold text-base !text-rolex-green">{t('specifications')} ({form.category})</Label>
                <div className="grid grid-cols-2 gap-3">
                  {(CATEGORY_SPECS[form.category] || CATEGORY_SPECS.other).map((key) => (
                    <div key={key} className="space-y-1">
                      <Label className="text-xs !text-rolex-green/70">{SPEC_LABELS[key] || key}</Label>
                      {key === 'items_included' || key === 'included' || key === 'papers' ? (
                        <textarea value={form.specs[key] || ''} onChange={(e) => setForm({...form, specs: {...form.specs, [key]: e.target.value}})} className={taCls + ' h-16 text-sm'} placeholder={SPEC_LABELS[key]} />
                      ) : (
                        <Input value={form.specs[key] || ''} onChange={(e) => setForm({...form, specs: {...form.specs, [key]: e.target.value}})} className={inputCls + ' text-sm'} placeholder={SPEC_LABELS[key]} />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping & Rules */}
              <div className="space-y-3 border-t border-border pt-4">
                <div className="space-y-2">
                  <Label className="font-semibold !text-rolex-green">{t('shipping')}</Label>
                  <textarea value={form.shipping_info} onChange={(e) => setForm({...form, shipping_info: e.target.value})} className={taCls + ' h-20'} placeholder="Shipping details for this product..." />
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold !text-rolex-green">{t('rules')}</Label>
                  <textarea value={form.rules_info} onChange={(e) => setForm({...form, rules_info: e.target.value})} className={taCls + ' h-20'} placeholder="Rules and conditions for this draw..." />
                </div>
              </div>

              {/* Images */}
              <div className="space-y-3 border-t border-border pt-4">
                <Label className="font-semibold !text-rolex-green">Images</Label>
                {form.imageUrls.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {form.imageUrls.map((url, idx) => (
                      <div key={idx} className="relative group">
                        <img src={url} alt="" className="w-full h-20 object-cover rounded-lg border border-border" />
                        <button onClick={() => removeImage(idx)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input type="file" ref={fileInputRef} multiple accept="image/*" onChange={handleUploadImages} className="hidden" />
                  <Button type="button" variant="outline" className="gap-2 rounded-xl flex-1" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                    <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : t('uploadImages')}
                  </Button>
                  <Button type="button" variant="outline" className="gap-2 rounded-xl" onClick={addImageUrl}>
                    <ImagePlus className="w-4 h-4" /> URL
                  </Button>
                </div>
              </div>

              <div className="space-y-2"><Label className="!text-rolex-green">{t('videoUrl')}</Label><Input value={form.video_url} onChange={(e) => setForm({...form, video_url: e.target.value})} className={inputCls + ' font-mono'} placeholder="https://youtube.com/..." /></div>
              <div className="flex items-center gap-2"><Switch checked={form.enabled} onCheckedChange={(v) => setForm({...form, enabled: v})} /><Label className="!text-rolex-green">{t('enabled')}</Label></div>

              {/* Question */}
              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex items-center gap-2"><Switch checked={form.q_enabled} onCheckedChange={(v) => setForm({...form, q_enabled: v})} /><Label className="font-semibold !text-rolex-green">{t('enableQuestion')}</Label></div>
                {form.q_enabled && (
                  <>
                    <div className="space-y-2"><Label className="!text-rolex-green">{t('questionText')}</Label><Input value={form.q_text} onChange={(e) => setForm({...form, q_text: e.target.value})} className={inputCls} placeholder="e.g. What model is this watch?" /></div>
                    <div className="space-y-2">
                      <Label className="!text-rolex-green">{t('questionImage')}</Label>
                      {form.q_image && (
                        <div className="relative inline-block">
                          <img src={form.q_image} alt="Question" className="h-24 rounded-lg border border-border" />
                          <button onClick={() => setForm({...form, q_image: ''})} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <input type="file" ref={questionFileRef} accept="image/*" onChange={handleUploadQuestionImage} className="hidden" />
                        <Button type="button" variant="outline" size="sm" className="gap-2 rounded-lg" onClick={() => questionFileRef.current?.click()} disabled={uploadingQuestion}>
                          <Upload className="w-3 h-3" /> {uploadingQuestion ? '...' : t('uploadImage')}
                        </Button>
                        <Input value={form.q_image} onChange={(e) => setForm({...form, q_image: e.target.value})} className={inputCls + ' text-xs flex-1'} placeholder="Or paste image URL..." />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1"><Label className="text-xs !text-rolex-green">{t('answer')} 1</Label><Input value={form.q_a1} onChange={(e) => setForm({...form, q_a1: e.target.value})} className={inputCls + ' text-sm'} /></div>
                      <div className="space-y-1"><Label className="text-xs !text-rolex-green">{t('answer')} 2</Label><Input value={form.q_a2} onChange={(e) => setForm({...form, q_a2: e.target.value})} className={inputCls + ' text-sm'} /></div>
                      <div className="space-y-1"><Label className="text-xs !text-rolex-green">{t('answer')} 3</Label><Input value={form.q_a3} onChange={(e) => setForm({...form, q_a3: e.target.value})} className={inputCls + ' text-sm'} /></div>
                      <div className="space-y-1"><Label className="text-xs !text-rolex-green">{t('answer')} 4</Label><Input value={form.q_a4} onChange={(e) => setForm({...form, q_a4: e.target.value})} className={inputCls + ' text-sm'} /></div>
                    </div>
                    <div className="space-y-2"><Label className="!text-rolex-green">{t('correctAnswer')} (0-3)</Label><Input type="number" min="0" max="3" value={form.q_correct} onChange={(e) => setForm({...form, q_correct: e.target.value})} className={inputCls + ' w-20'} /></div>
                  </>
                )}
              </div>

              {/* Ticket Packages */}
              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Label className="font-semibold !text-rolex-green">{t('ticketPackages')}</Label>
                  <div className="flex gap-1.5 flex-wrap">
                    {templates.length > 0 && (
                      <div className="relative">
                        <Button type="button" size="sm" variant="outline" className="gap-1 text-xs rounded-lg" onClick={() => setShowTemplateMenu(!showTemplateMenu)}>
                          📑 {t('useTemplate') || 'Use template'}
                        </Button>
                        {showTemplateMenu && (
                          <div className="absolute right-0 top-full mt-1 z-50 w-72 bg-white border border-[hsl(var(--gold)/0.4)] rounded-xl shadow-xl overflow-hidden">
                            <div className="px-3 py-2 bg-[hsl(43_30%_94%)] border-b border-[hsl(var(--gold)/0.3)]">
                              <p className="text-[10px] uppercase tracking-[0.2em] font-bold !text-rolex-green/70">{t('availableTemplates') || 'Available templates'}</p>
                            </div>
                            <div className="max-h-72 overflow-y-auto">
                              {templates.map((tpl) => (
                                <div key={tpl._id} className="flex items-center gap-2 px-3 py-2 border-b border-[hsl(var(--gold)/0.15)] hover:bg-[hsl(var(--gold)/0.08)]">
                                  <button type="button" onClick={() => applyTemplate(tpl)} className="flex-1 text-left">
                                    <p className="font-semibold text-sm !text-rolex-green">{tpl.name}</p>
                                    <p className="text-[10px] !text-rolex-green/55">
                                      {(tpl.packages || []).map(p => `${p.qty}×${p.discount_pct}%`).join(' · ')}
                                    </p>
                                  </button>
                                  <button type="button" onClick={() => handleDeleteTemplate(tpl._id)} className="p-1 hover:bg-red-100 rounded text-red-500" title="Delete template">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <Button type="button" size="sm" variant="outline" className="gap-1 text-xs rounded-lg" onClick={saveAsTemplate} title={t('saveAsTemplateHelp') || 'Save current packages as a reusable template'}>
                      <BookmarkPlus className="w-3.5 h-3.5" /> {t('saveAsTemplate') || 'Save as template'}
                    </Button>
                    <Button size="sm" variant="ghost" className="gap-1 text-xs" onClick={() => setForm(prev => ({...prev, ticket_packages: [...prev.ticket_packages, {qty: 10, discount_pct: 10, label: ''}]}))}>
                      <Plus className="w-3 h-3" /> {t('addPackage')}
                    </Button>
                  </div>
                </div>
                {form.ticket_packages.length === 0 && (
                  <div className="rounded-xl bg-[hsl(43_30%_94%)] border border-[hsl(var(--gold)/0.25)] p-3 text-xs !text-rolex-green/70">
                    💡 {t('noPackagesYet') || 'No packages yet. Add manually with "+ Add package" or apply a template.'}
                  </div>
                )}
                {form.ticket_packages.map((pkg, idx) => (
                  <div key={idx} className="flex gap-2 items-end">
                    <div className="space-y-1 flex-1"><Label className="text-xs !text-rolex-green">{t('qty')}</Label><Input type="number" value={pkg.qty} onChange={(e) => { const pkgs = [...form.ticket_packages]; pkgs[idx] = {...pkgs[idx], qty: parseInt(e.target.value) || 0}; setForm({...form, ticket_packages: pkgs}); }} className={inputCls + ' text-sm'} /></div>
                    <div className="space-y-1 flex-1"><Label className="text-xs !text-rolex-green">{t('discountPct')}</Label><Input type="number" value={pkg.discount_pct} onChange={(e) => { const pkgs = [...form.ticket_packages]; pkgs[idx] = {...pkgs[idx], discount_pct: parseInt(e.target.value) || 0}; setForm({...form, ticket_packages: pkgs}); }} className={inputCls + ' text-sm'} /></div>
                    <div className="space-y-1 flex-1"><Label className="text-xs !text-rolex-green">Label</Label><Input value={pkg.label || ''} onChange={(e) => { const pkgs = [...form.ticket_packages]; pkgs[idx] = {...pkgs[idx], label: e.target.value}; setForm({...form, ticket_packages: pkgs}); }} className={inputCls + ' text-sm'} placeholder="e.g. Best Value" /></div>
                    <Button size="icon" variant="ghost" className="text-red-400 shrink-0 mb-0.5" onClick={() => setForm(prev => ({...prev, ticket_packages: prev.ticket_packages.filter((_, i) => i !== idx)}))}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">{t('cancel')}</Button>
              <Button className="bg-primary text-primary-foreground btn-press rounded-xl" onClick={handleSubmit}>{editRaffle ? t('update') : t('create')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* NOTIFY PARTICIPANTS CONFIRMATION DIALOG */}
        <Dialog open={notifyDialog.open} onOpenChange={(o) => !o && setNotifyDialog({ open: false, payload: null })}>
          <DialogContent className="bg-white border-border max-w-md" style={{ color: '#0B3D2E' }} data-testid="notify-participants-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2 !text-rolex-green">
                <Mail className="w-5 h-5 text-gold" /> {t('notifyParticipantsTitle') || 'Notify participants?'}
              </DialogTitle>
              <DialogDescription className="text-sm !text-rolex-green/70 pt-1">
                {t('notifyParticipantsDesc') || 'This change (draw date / sold out) can be announced by email to all participants. Do you want to send it?'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" className="rounded-xl" data-testid="save-without-email-btn"
                onClick={() => performSave(notifyDialog.payload, false)}>
                {t('saveWithoutEmail') || 'Save without email'}
              </Button>
              <Button className="bg-primary text-primary-foreground btn-press rounded-xl" data-testid="save-and-send-email-btn"
                onClick={() => performSave(notifyDialog.payload, true)}>
                <Send className="w-4 h-4 mr-2" /> {t('saveAndSendEmail') || 'Save and send email'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* DESIGNATE WINNER DIALOG */}
        <Dialog open={designateDialog.open} onOpenChange={(o) => !o && setDesignateDialog({ open: false, raffle: null })}>
          <DialogContent className="bg-white border-border max-w-3xl max-h-[90vh] overflow-y-auto" style={{ color: '#0B3D2E' }}>
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2 !text-rolex-green">
                <Sparkles className="w-5 h-5 text-gold" /> {t('designateWinner') || 'Designate Winner'}
              </DialogTitle>
              <DialogDescription className="text-sm !text-rolex-green/70 pt-1">
                <strong>{designateDialog.raffle?.title}</strong> — {(designateDialog.raffle?.sold_tickets || 0)} tickets · {participants.length} participants
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-3">
              {/* Mode selector */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setDesignateMode('code')}
                  className={`p-3 rounded-xl border-2 transition-colors text-left ${
                    designateMode === 'code'
                      ? 'border-gold bg-[hsl(var(--gold)/0.10)]'
                      : 'border-[hsl(var(--gold)/0.20)] bg-[hsl(43_30%_94%)]'
                  }`}
                  data-testid="mode-code"
                >
                  <p className="font-bold text-sm !text-rolex-green flex items-center gap-1.5"><FileSpreadsheet className="w-4 h-4 text-gold" /> {t('byTicketCode') || 'By winning ticket code'}</p>
                  <p className="text-[11px] !text-rolex-green/60 mt-1">{t('byTicketCodeHelp') || 'Paste the code returned by your draw system'}</p>
                </button>
                <button
                  type="button"
                  onClick={() => setDesignateMode('manual')}
                  className={`p-3 rounded-xl border-2 transition-colors text-left ${
                    designateMode === 'manual'
                      ? 'border-gold bg-[hsl(var(--gold)/0.10)]'
                      : 'border-[hsl(var(--gold)/0.20)] bg-[hsl(43_30%_94%)]'
                  }`}
                  data-testid="mode-manual"
                >
                  <p className="font-bold text-sm !text-rolex-green flex items-center gap-1.5"><Users className="w-4 h-4 text-gold" /> {t('manualSelection') || 'Manual selection'}</p>
                  <p className="text-[11px] !text-rolex-green/60 mt-1">{t('manualSelectionHelp') || 'Pick a user and a specific ticket'}</p>
                </button>
              </div>

              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex gap-2">
                <Sparkles className="w-4 h-4 shrink-0 mt-0.5" />
                <p>{t('designateExplain')}</p>
              </div>

              {designateMode === 'code' ? (
                <div className="space-y-2">
                  <Label className="!text-rolex-green text-sm font-semibold">{t('winningTicketCode') || 'Winning ticket code'}</Label>
                  <Input
                    placeholder="412-09"
                    value={winningCodeInput}
                    onChange={(e) => setWinningCodeInput(e.target.value.trim())}
                    className={inputCls + ' font-mono text-center text-2xl font-bold tracking-widest h-16'}
                    data-testid="winning-code-input"
                  />
                  <p className="text-[11px] !text-rolex-green/60">{t('winningTicketCodeHelp') || 'Enter the code returned by your external draw system. The system will automatically find the user who owns this ticket.'}</p>
                </div>
              ) : (
                <>
                  <Input
                    placeholder={t('searchParticipants') || 'Search by name or email...'}
                    value={participantSearch}
                    onChange={(e) => setParticipantSearch(e.target.value)}
                    className={inputCls}
                  />
                  {loadingParticipants ? (
                    <div className="text-center py-10 text-sm text-rolex-green/60">{t('loadingParticipants') || 'Loading participants...'}</div>
                  ) : filteredParticipants.length === 0 ? (
                    <div className="text-center py-10">
                      <Users className="w-10 h-10 mx-auto mb-2 text-rolex-green/30" />
                      <p className="text-sm !text-rolex-green/60">{t('noParticipantsYet') || 'No participants for this raffle yet.'}</p>
                    </div>
                  ) : (
                    <div className="border border-[hsl(var(--gold)/0.25)] rounded-xl overflow-hidden max-h-[400px] overflow-y-auto">
                      {filteredParticipants.map((p) => {
                        const isSelected = selectedWinnerUser === p.user_id;
                        return (
                          <div key={p.user_id} className="border-b border-[hsl(var(--gold)/0.15)]">
                            <button
                              type="button"
                              onClick={() => { setSelectedWinnerUser(p.user_id); setSelectedTicketNumber(null); }}
                              className={`w-full flex items-center gap-3 p-3 transition-colors text-left ${
                                isSelected ? 'bg-[hsl(var(--gold)/0.18)]' : 'bg-white hover:bg-[hsl(var(--gold)/0.06)]'
                              }`}
                              data-testid={`select-participant-${p.user_id}`}
                            >
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${
                                isSelected ? 'bg-gold-gradient text-rolex-green' : 'bg-rolex-green text-cream'
                              }`}>
                                {(p.first_name?.[0] || p.email?.[0] || '?').toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm !text-rolex-green truncate">{p.first_name} {p.last_name}</p>
                                <p className="text-xs !text-rolex-green/60 truncate">{p.email}</p>
                              </div>
                              <Badge className="bg-gold-gradient !text-rolex-green border-none font-mono text-[11px] shrink-0">
                                🎟️ {p.total_tickets}
                              </Badge>
                            </button>
                            {isSelected && p.ticket_codes && p.ticket_codes.length > 0 && (
                              <div className="bg-[hsl(43_30%_96%)] p-3 border-t border-[hsl(var(--gold)/0.15)]">
                                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-rolex-green/60 mb-2">{t('selectTheirTicket') || 'Select their winning ticket'}</p>
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                                  {p.ticket_codes.map((tc) => (
                                    <button
                                      key={tc.number}
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); setSelectedTicketNumber(tc.number); }}
                                      className={`text-xs font-mono py-1.5 rounded border transition-colors ${
                                        selectedTicketNumber === tc.number
                                          ? 'bg-gold-gradient text-rolex-green border-gold font-bold'
                                          : 'bg-white border-[hsl(var(--gold)/0.3)] text-rolex-green hover:border-gold'
                                      }`}
                                    >
                                      #{tc.code}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDesignateDialog({ open: false, raffle: null })} className="rounded-xl">{t('cancel')}</Button>
              <Button
                className="btn-gold rounded-xl gap-1 font-bold"
                onClick={confirmDesignate}
                disabled={designating || (designateMode === 'code' ? !winningCodeInput.trim() : !selectedWinnerUser)}
                data-testid="confirm-designate-btn"
              >
                <Sparkles className="w-4 h-4" />
                {designating ? (t('processing') || 'Processing...') : (t('confirmWinner') || 'Confirm winner & notify')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* RELANCE — choose the "tickets left" number shown in the email */}
        <Dialog open={!!relanceDialog} onOpenChange={(o) => !o && setRelanceDialog(null)}>
          <DialogContent className="bg-white border-border max-w-md" style={{ color: '#0B3D2E' }} data-testid="relance-tickets-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2 !text-rolex-green">
                <Send className="w-5 h-5 text-orange-600" /> {t('relanceEmailTitle') || 'Email de relance'}
              </DialogTitle>
              <DialogDescription className="text-sm !text-rolex-green/70 pt-1">
                {t('relanceEmailDesc') || 'Combien de tickets restants voulez-vous afficher dans le mail ? Vous verrez ensuite un aperçu exact avant l\'envoi.'}
              </DialogDescription>
            </DialogHeader>
            {relanceDialog && (
              <div className="space-y-3 py-2">
                <div className="space-y-2">
                  <Label className="!text-rolex-green">{t('ticketsLeftToDisplay') || 'Tickets restants affichés dans le mail'}</Label>
                  <Input
                    type="number"
                    min={1}
                    max={relanceDialog.max}
                    value={relanceDialog.count}
                    onChange={(e) => setRelanceDialog({ ...relanceDialog, count: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && confirmRelanceCount()}
                    className={inputCls + ' text-lg font-bold'}
                    autoFocus
                    data-testid="relance-tickets-input"
                  />
                  <p className="text-xs !text-rolex-green/60">
                    {t('realTicketsLeft') || 'Réellement restants'} : <span className="font-mono font-bold text-[#A07828]">{relanceDialog.realLeft}</span> / {relanceDialog.max}
                  </p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" className="rounded-xl" onClick={() => setRelanceDialog(null)} data-testid="relance-cancel-btn">{t('cancel')}</Button>
              <Button className="bg-primary text-primary-foreground btn-press rounded-xl" onClick={confirmRelanceCount} data-testid="relance-preview-btn">
                <Eye className="w-4 h-4 mr-2" /> {t('seeEmailPreview') || 'Voir l\'aperçu du mail'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Email preview modal — opens before sending any client email */}
        {emailPreview && (
          <EmailPreviewDialog
            open={!!emailPreview}
            onClose={closeEmailPreview}
            raffle={emailPreview.raffle}
            emailType={emailPreview.type}
            typeLabel={emailPreview.label}
            typeIcon={emailPreview.icon}
            onSend={emailPreview.sendFn}
            ticketsOverride={emailPreview.ticketsOverride}
          />
        )}
      </main>
    </div>
  );
}
